import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { PlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import { Checkbox } from '@/components/checkbox/checkbox';
import { Spinner } from '@/components/spinner/spinner';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  CreateProductClaimSchema,
  createProductClaimMutationOptions,
} from '@/features/product-claim/api/create-product-claim.api';
import { GET_LIST_KLAIM_PRODUK_QUERY_KEY } from '@/features/product-claim/api/get-list-product-claim.api';
import { getListProductQueryOptions } from '@/features/products/api/get-list-product.api';
import type { Product } from '@/features/products/types/product.type';
import ProductClaimUserSelector from './product-claim-user-selector';

const ALLOW_MODE_OPTIONS = [
  { value: 'ALL', label: 'Semua User' },
  { value: 'WHITELIST', label: 'User Tertentu' },
];

export interface CreateProductClaimDialogProps extends BaseDialogProps {}

export default function CreateProductClaimDialog({
  dialog,
}: CreateProductClaimDialogProps) {
  const { closeCreateProductClaimDialog } = useV2DialogContext();
  const queryClient = useQueryClient();
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productSearch, setProductSearch] = useState('');

  const createProductClaimMutation = useMutation({
    ...createProductClaimMutationOptions(),
    onSuccess: () => {
      toast.success('Klaim produk berhasil ditambahkan');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_KLAIM_PRODUK_QUERY_KEY],
      });
      form.reset();
      setSelectedProduct(null);
      closeCreateProductClaimDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menambahkan klaim produk';
      toast.error(message);
    },
  });

  const productsQuery = useQuery({
    ...getListProductQueryOptions({ limit: 100, search: productSearch }),
    enabled: showProductSelector,
  });

  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      max_quota: 1,
      allow_mode: 'ALL' as 'ALL' | 'WHITELIST',
      allow_lists: [] as string[],
      is_active: true,
      starts_at: '',
      starts_at_time: '00:00',
      expired_at: '',
      expired_at_time: '23:59',
    },
    onSubmit: async ({ value }) => {
      const {
        starts_at_time,
        expired_at_time,
        allow_mode,
        allow_lists,
        ...rest
      } = value;
      const isListMode = allow_mode !== 'ALL';
      const parsed = CreateProductClaimSchema.safeParse({
        ...rest,
        allow_mode,
        allow_type: isListMode ? 'EMAIL' : undefined,
        allow_lists: isListMode ? allow_lists : undefined,
        product_id: selectedProduct?.id ?? '',
        starts_at: new Date(
          `${value.starts_at}T${starts_at_time}:00`
        ).toISOString(),
        expired_at: new Date(
          `${value.expired_at}T${expired_at_time}:00`
        ).toISOString(),
      });
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Data klaim produk tidak valid'
        );
        return;
      }
      await createProductClaimMutation.mutateAsync(parsed.data);
    },
  });

  const isPending = createProductClaimMutation.isPending;

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowProductSelector(false);
  };

  const handleRemoveProduct = () => {
    setSelectedProduct(null);
  };

  return (
    <>
      <Dialog
        {...dialog}
        onOpenChange={nextOpenState => {
          if (!nextOpenState) {
            closeCreateProductClaimDialog();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tambah Klaim Produk</DialogTitle>
            <DialogDescription>
              Lengkapi data berikut untuk menambahkan klaim produk baru.
            </DialogDescription>
          </DialogHeader>

          <form
            className="flex flex-col gap-4"
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field name="code">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Code</Label>
                  <Input
                    id={field.name}
                    placeholder="Masukkan Code"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                    className="uppercase"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="name">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Judul</Label>
                  <Input
                    id={field.name}
                    placeholder="Masukkan Judul"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="max_quota">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Kuota</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id={field.name}
                      type="number"
                      min={1}
                      placeholder="Masukkan Kuota"
                      value={field.state.value === -1 ? '' : field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => {
                        if (e.target.value === '') {
                          field.handleChange(-1);
                        } else {
                          field.handleChange(e.target.valueAsNumber || 0);
                        }
                      }}
                      disabled={field.state.value === -1}
                      className="flex-1"
                    />
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="unlimited_quota"
                        checked={field.state.value === -1}
                        onCheckedChange={checked => {
                          if (checked) {
                            field.handleChange(-1);
                          } else {
                            field.handleChange(1);
                          }
                        }}
                      />
                      <Label
                        htmlFor="unlimited_quota"
                        className="cursor-pointer"
                      >
                        Unlimited
                      </Label>
                    </div>
                  </div>
                </div>
              )}
            </form.Field>

            <form.Field name="allow_mode">
              {field => (
                <div className="grid gap-2">
                  <Label>Mode Akses</Label>
                  <Select
                    className="w-full"
                    options={ALLOW_MODE_OPTIONS}
                    value={field.state.value}
                    onValueChange={value =>
                      field.handleChange(value as 'ALL' | 'WHITELIST')
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={state => [state.values.allow_mode]}>
              {([allowMode]) =>
                allowMode !== 'ALL' && (
                  <form.Field name="allow_lists">
                    {field => (
                      <div className="grid gap-2">
                        <Label>Daftar User</Label>
                        <ProductClaimUserSelector
                          value={field.state.value}
                          onChange={field.handleChange}
                        />
                      </div>
                    )}
                  </form.Field>
                )
              }
            </form.Subscribe>

            <div className="grid gap-2">
              <Label>Produk</Label>
              {selectedProduct && (
                <div className="flex items-center gap-2 px-2 py-1 bg-muted rounded-md text-sm">
                  {selectedProduct.thumbnail_url && (
                    <img
                      src={selectedProduct.thumbnail_url}
                      alt=""
                      className="size-8 rounded object-cover"
                    />
                  )}
                  <span className="truncate flex-1">
                    {selectedProduct.title || selectedProduct.id}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveProduct}
                    className="hover:text-destructive"
                  >
                    <XIcon className="size-4" />
                  </button>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowProductSelector(true)}
              >
                <PlusIcon className="size-4" />
                {selectedProduct ? 'Ubah Produk' : 'Pilih Produk'}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field name="starts_at">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Tanggal Mulai</Label>
                    <Input
                      id={field.name}
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="starts_at_time">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Jam Mulai</Label>
                    <Input
                      id={field.name}
                      type="time"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field name="expired_at">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Tanggal Kadaluarsa</Label>
                    <Input
                      id={field.name}
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="expired_at_time">
                {field => (
                  <div className="grid gap-2">
                    <Label htmlFor={field.name}>Jam Kadaluarsa</Label>
                    <Input
                      id={field.name}
                      type="time"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeCreateProductClaimDialog}
                disabled={isPending}
              >
                Batal
              </Button>
              <form.Subscribe selector={state => [state.canSubmit]}>
                {([canSubmit]) => (
                  <Button
                    type="submit"
                    disabled={!canSubmit || isPending || !selectedProduct}
                  >
                    {isPending ? 'Menyimpan...' : 'Tambah'}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showProductSelector} onOpenChange={setShowProductSelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih Produk</DialogTitle>
            <DialogDescription>
              Pilih produk yang akan ditambahkan ke klaim produk.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Cari produk..."
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
          />
          <div className="max-h-[300px] overflow-y-auto">
            {productsQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : productsQuery.data?.result.data.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Tidak ada produk tersedia
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {productsQuery.data?.result.data.map(product => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleSelectProduct(product)}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
                  >
                    {product.thumbnail_url && (
                      <img
                        src={product.thumbnail_url}
                        alt=""
                        className="size-10 rounded object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {product.title || 'Untitled'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {product.short_description || product.id}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
